import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, useAlerts } from '@tradingblock/components';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import { getResponseCodeDescription } from '../../../utilities/action';

export const RemoveAllBeneficiaries: React.FunctionComponent<{
  show?: boolean;
  /** Must honor boolean (Modal calls setOpen(false) on hide; toggle would reopen). */
  setOpen: (open: boolean) => void;
  /** Target account (e.g. linked retirement account when applicable). */
  accountId: number | undefined;
}> = ({ show, setOpen, accountId }) => {
  const dispatch = useDispatch();
  const notifications = useAlerts();
  const removeAllPendingRef = useRef(false);

  const isUpdateLoading = useStateSelector(s => s.accountManagement.update.isLoading);
  const updateData = useStateSelector(s => s.accountManagement.update.data);
  const updateResponseCode = useStateSelector(s => s.accountManagement.update.responseCode);
  const updateLoaded = useStateSelector(s => s.accountManagement.update.isLoaded);

  useEffect(() => {
    if (!removeAllPendingRef.current) {
      return;
    }
    if (isUpdateLoading) {
      return;
    }

    const payload = updateData as { errors?: string[]; payload?: { errors?: string[] } } | undefined;
    const payloadErrorsRm = payload != null ? payload.errors : undefined;
    const nestedErrorsRm =
      payload != null && payload.payload != null ? payload.payload.errors : undefined;
    const firstPayloadError =
      (Array.isArray(payloadErrorsRm) && payloadErrorsRm[0]) ||
      (Array.isArray(nestedErrorsRm) && nestedErrorsRm[0]);

    if (updateLoaded === false && updateData && typeof updateData === 'object' && 'error' in updateData) {
      const raw = (updateData as { error: unknown }).error;
      const message =
        (typeof raw === 'object' &&
          raw !== null &&
          'message' in raw &&
          typeof (raw as { message?: string }).message === 'string' &&
          (raw as { message: string }).message) ||
        (typeof raw === 'string' ? raw : null) ||
        'Unable to remove beneficiaries. Please try again.';
      console.error('[RemoveAllBeneficiaries] Account update request failed', raw);
      notifications.error(message);
      removeAllPendingRef.current = false;
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
      return;
    }

    if (updateLoaded === true && (updateResponseCode === 0 || updateResponseCode === 99)) {
      console.log('[RemoveAllBeneficiaries] Remove all beneficiaries completed', { responseCode: updateResponseCode });
      // Same as save: 0 and 99 are success codes from accounts.update (middleware refetches on both).
      notifications.success('All beneficiaries were removed.');
      removeAllPendingRef.current = false;
      setOpen(false);
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
      return;
    }

    if (
      updateLoaded === true &&
      updateResponseCode !== undefined &&
      updateResponseCode !== 0 &&
      updateResponseCode !== 99
    ) {
      const description = getResponseCodeDescription(
        updateResponseCode,
        typeof firstPayloadError === 'string'
          ? firstPayloadError
          : 'Unable to remove beneficiaries. Please try again.'
      );
      console.warn('[RemoveAllBeneficiaries] Remove beneficiaries rejected', {
        responseCode: updateResponseCode,
        description,
      });
      notifications.error(description);
      removeAllPendingRef.current = false;
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
    }
  }, [dispatch, isUpdateLoading, notifications, setOpen, updateData, updateLoaded, updateResponseCode]);

  const onClick = () => {
    if (accountId == null) {
      console.error('[RemoveAllBeneficiaries] Missing account id');
      notifications.error('Unable to remove beneficiaries. Account is not available.');
      return;
    }

    const updateValues = {
      item: 'RemoveBeneficiaries',
    };

    removeAllPendingRef.current = true;
    dispatch(AccountManagementDataActions.requestMakeAccountUpdate({ accountId, request: updateValues }));
  };

  const removeInFlight = isUpdateLoading && removeAllPendingRef.current;

  return (
    <Modal setShow={setOpen} show={show}>
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title>Remove All Beneficiaries?</BootstrapModal.Title>
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        <p>
          <i className="far fa-question-circle" />{' '}
          <strong>Are you sure you want to remove all current beneficiaries?</strong>
        </p>
      </BootstrapModal.Body>
      <BootstrapModal.Footer>
        <Button variant="secondary" onClick={() => setOpen(false)} disabled={!!removeInFlight}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onClick} disabled={!!removeInFlight}>
          {removeInFlight ? 'Removing…' : 'Remove'}
        </Button>
      </BootstrapModal.Footer>
    </Modal>
  );
};
